import { useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PRIMARY_CITIES_DROPDOWN } from "../data/site";
import { track } from "../lib/track";
import { getAttribution } from "../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "New Deck",
  "Deck Replacement / Redeck",
  "Composite / PVC Deck",
  "Covered Deck / Roof Structure",
  "Screened Porch",
  "Railing",
  "Stairs",
  "Outdoor Living Project",
  "Other",
];

const BUDGETS = [
  "Under $20,000",
  "$20,000–$35,000",
  "$35,000–$50,000",
  "$50,000–$75,000",
  "$75,000–$100,000",
  "$100,000+",
  "Not sure yet",
];

const TIMELINES = [
  "As soon as possible",
  "Within 1–3 months",
  "Within 3–6 months",
  "6+ months",
  "Just researching",
];

const initialState = {
  name: "",
  phone: "",
  email: "",
  city: "",
  address: "",
  project_type: "",
  budget: "",
  timeline: "",
  approx_size: "",
  material_preference: "",
  contact_pref: "",
  project_description: "",
  photos: [],
  consent: false,
  financing_interest: false,
};

export default function LeadForm({ compact = false }) {
  const [form, setForm] = useState(initialState);
  const [hp, setHp] = useState(""); // honeypot
  const [step, setStep] = useState(1); // 1 = required contact info, 2 = optional details
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const successRef = useRef(null);
  const startedRef = useRef(false);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("estimate_form_started", { page: window.location.pathname });
  };

  // Bring the confirmation panel into view (mobile: it replaces a taller form)
  useEffect(() => {
    if (done) successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [done]);

  // Carry over a TimberTech product interest selected in the product explorer
  // (no account needed — stored in sessionStorage).
  useEffect(() => {
    try {
      const interest = sessionStorage.getItem("tt_interest");
      if (interest) {
        setForm((s) =>
          s.project_description ? s : { ...s, project_description: interest }
        );
      }
    } catch (_) {}
    // Financing interest handoff from /financing CTAs ("/contact?financing=1")
    try {
      if (new URLSearchParams(window.location.search).get("financing") === "1") {
        setForm((s) => ({ ...s, financing_interest: true }));
        track("financing_estimate_request");
      }
    } catch (_) {}
  }, []);

  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const addPhotos = async (files) => {
    const list = Array.from(files || []).slice(0, 3 - form.photos.length);
    for (const f of list) {
      if (!f.type.startsWith("image/")) continue;
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`"${f.name}" is over 5 MB — skipped.`);
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads`, fd);
        setForm((s) => ({ ...s, photos: [...s.photos, data.url] }));
      } catch {
        toast.error("Photo upload failed — you can submit without it.");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const removePhoto = (url) =>
    setForm((s) => ({ ...s, photos: s.photos.filter((u) => u !== url) }));

  const validateRequired = () => {
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please complete all required fields.");
      return false;
    }
    if (!form.city && !form.address) {
      toast.error("Please choose your city or enter your project address / ZIP.");
      return false;
    }
    if (!form.consent) {
      toast.error("Please confirm we may contact you about your estimate.");
      return false;
    }
    return true;
  };

  const goToDetails = () => {
    if (!validateRequired()) return;
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateRequired()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        source: "estimate_form",
        ...form,
        ...getAttribution(),
        page: window.location.pathname,
        honeypot: hp,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      track("generate_lead", { form: "estimate", project_type: form.project_type });
      track("estimate_form_submitted", { project_type: form.project_type, page: window.location.pathname });
      if (window.location.pathname === "/contact") track("contact_form_submitted", { project_type: form.project_type });
      if (form.financing_interest) track("financing_interest_lead", { project_type: form.project_type });
      setDone(true);
      setForm(initialState);
      setStep(1);
      try { sessionStorage.removeItem("tt_interest"); } catch (_) {}
      toast.success("Thanks — we received your project request.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call (678) 356-7281.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        ref={successRef}
        data-testid="lead-form-success"
        className="bg-white border border-[#E7E2D9] p-10 md:p-14 shadow-[0_24px_60px_-30px_rgba(17,17,17,0.18)]"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
          Request Received
        </p>
        <h3 className="font-display text-3xl md:text-4xl mt-4 tracking-tight text-[#111]">
          Thanks — we received your project request.
        </h3>
        <p className="mt-4 text-[#4A4A4A] leading-relaxed">
          A member of Zarco's Decks & Outdoor Living will review your
          information and reach out to discuss the next step. Need to reach us
          sooner? Call or text{" "}
          <a
            href="tel:+16783567281"
            data-testid="lead-success-call-link"
            onClick={() => track("click_to_call", { location: "form_success" })}
            className="text-[#99582A] underline"
          >
            (678) 356-7281
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          data-testid="lead-form-submit-another"
          className="mt-8 border-2 border-[#111] px-6 py-3 text-sm font-semibold hover:bg-[#111] hover:text-white transition-colors"
        >
          Submit another project
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={markStarted}
      data-testid="lead-form"
      noValidate
      className={`bg-white ${compact ? "p-6 md:p-8" : "p-8 md:p-12"} border border-[#E7E2D9] shadow-[0_24px_60px_-30px_rgba(17,17,17,0.18)]`}
    >
      <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
        Free Estimate
      </p>
      <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-2 text-[#111]">
        Tell us about your project.
      </h3>
      <p className="text-sm text-[#4A4A4A] mt-2">
        We'll respond within 24 hours, Mon–Sat.
      </p>
      <p data-testid="lead-form-step-indicator" className="mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#99582A]">
        Step {step} of 2 — {step === 1 ? "Your contact info" : "Optional project details"}
      </p>

      {step === 1 && (
      <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="lead-form-step-1">
        <Field label="Full Name *" testId="field-name">
          <input
            type="text"
            required
            value={form.name}
            onChange={setField("name")}
            data-testid="lead-form-name"
            className={inputCls}
            placeholder="Jane Doe"
          />
        </Field>

        <Field label="Phone Number *" testId="field-phone">
          <input
            type="tel"
            required
            value={form.phone}
            onChange={setField("phone")}
            data-testid="lead-form-phone"
            className={inputCls}
            placeholder="(770) 555-0123"
          />
        </Field>

        <Field label="Email Address *" testId="field-email">
          <input
            type="email"
            required
            value={form.email}
            onChange={setField("email")}
            data-testid="lead-form-email"
            className={inputCls}
            placeholder="you@email.com"
          />
        </Field>

        <Field label="City *" testId="field-city">
          <select
            value={form.city}
            onChange={setField("city")}
            data-testid="lead-form-city"
            className={inputCls}
          >
            <option value="" disabled>Choose your city</option>
            {PRIMARY_CITIES_DROPDOWN.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>

        <Field label="Project Type *" testId="field-project-type">
          <select
            required
            value={form.project_type}
            onChange={setField("project_type")}
            data-testid="lead-form-project-type"
            className={inputCls}
          >
            <option value="" disabled>Select project type</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
      </div>
      )}

      {step === 2 && (
      <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="lead-form-step-2">
        <Field label="Project Address or ZIP (optional)" testId="field-address">
          <input
            type="text"
            value={form.address}
            onChange={setField("address")}
            data-testid="lead-form-address"
            className={inputCls}
            placeholder="Street address or ZIP code"
          />
        </Field>

        <Field label="Approximate Project Size" testId="field-approx-size">
          <select
            value={form.approx_size}
            onChange={setField("approx_size")}
            data-testid="lead-form-approx-size"
            className={inputCls}
          >
            <option value="">Not sure</option>
            {["Under 200 sq ft", "200–400 sq ft", "400–600 sq ft", "600+ sq ft"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>

        <Field label="Preferred Decking Material" testId="field-material">
          <select
            value={form.material_preference}
            onChange={setField("material_preference")}
            data-testid="lead-form-material"
            className={inputCls}
          >
            <option value="">Not sure — help me choose</option>
            {["TimberTech Composite", "TimberTech Advanced PVC", "Trex", "Wood", "Other"].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>

        <Field label="Approximate Project Investment" testId="field-budget">
          <select
            value={form.budget}
            onChange={setField("budget")}
            data-testid="lead-form-budget"
            className={inputCls}
          >
            <option value="">Select investment range</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </Field>

        <Field label="Desired Project Timeline" testId="field-timeline">
          <select
            value={form.timeline}
            onChange={setField("timeline")}
            data-testid="lead-form-timeline"
            className={inputCls}
          >
            <option value="">Select timeline</option>
            {TIMELINES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>

        <Field label="Preferred Contact Method" testId="field-contact-pref">
          <select
            value={form.contact_pref}
            onChange={setField("contact_pref")}
            data-testid="lead-form-contact-pref"
            className={inputCls}
          >
            <option value="">No preference</option>
            {["Phone call", "Text message", "Email"].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>
      </div>
      )}

      {step === 2 && (<>
      <div className="mt-6">
        <Field label="Project Description (optional)" testId="field-project-description">
          <textarea
            rows={4}
            value={form.project_description}
            onChange={setField("project_description")}
            data-testid="lead-form-project-description"
            className={`${inputCls} resize-none`}
            placeholder="Tell us about your project — size, materials (TimberTech, Trex, wood, etc.), timing, and anything else we should know."
          />
        </Field>
      </div>

      {/* Optional photo upload */}
      <div className="mt-6">
        <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#2A2A2A]">
          Photos (optional)
        </p>
        <p className="text-xs text-[#666] mt-1">
          Attach current deck, backyard, or inspiration photos — up to 3, 5 MB each.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {form.photos.map((u) => (
            <span key={u} className="relative w-20 h-20 border border-[#E7E2D9] overflow-hidden group">
              <img src={u} alt="Uploaded project photo" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(u)}
                aria-label="Remove photo"
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/70 text-white flex items-center justify-center"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {form.photos.length < 3 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="lead-form-photos-btn"
              className="w-20 h-20 border-2 border-dashed border-[#E7E2D9] hover:border-[#99582A] flex flex-col items-center justify-center gap-1 text-[#99582A] transition-colors"
            >
              {uploading > 0 ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              <span className="text-[9px] font-semibold tracking-wider">ADD PHOTO</span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="lead-form-photos-input"
            onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
          />
        </div>
      </div>
      </>)}

      {/* Consent */}
      {step === 1 && (
      <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="lead-form-consent-wrap">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => setForm((s) => ({ ...s, consent: e.target.checked }))}
          data-testid="lead-form-consent"
          className="mt-1 w-4 h-4 accent-[#99582A]"
        />
        <span className="text-xs text-[#4A4A4A] leading-relaxed">
          I agree that Zarco's Decks & Outdoor Living may contact me by phone,
          text, or email about my estimate request. Message/data rates may
          apply. No spam — ever. See our{" "}
          <a href="/privacy" className="underline text-[#99582A]">Privacy Policy</a>.
        </span>
      </label>
      )}

      {/* Financing interest — optional, never required */}
      {step === 1 && (
      <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="lead-form-financing-wrap">
        <input
          type="checkbox"
          checked={form.financing_interest}
          onChange={(e) => setForm((s) => ({ ...s, financing_interest: e.target.checked }))}
          data-testid="lead-form-financing"
          className="mt-1 w-4 h-4 accent-[#99582A]"
        />
        <span className="text-xs text-[#4A4A4A] leading-relaxed">
          Would financing be helpful for this project?{" "}
          <span className="font-semibold text-[#111]">Yes, I'd like to discuss financing.</span>{" "}
          (optional)
        </span>
      </label>
      )}

      {/* honeypot — hidden field bots often fill */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="absolute -left-[9999px] w-px h-px opacity-0"
        aria-hidden="true"
      />

      {step === 1 ? (
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
          <button
            type="submit"
            disabled={submitting || uploading > 0}
            data-testid="lead-form-submit-btn"
            className="w-full md:w-auto bg-[#111] text-white hover:bg-[#99582A] transition-colors px-10 py-4 font-semibold tracking-wide flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Get Your Free Estimate
                <ArrowRight size={18} />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={goToDetails}
            data-testid="lead-form-step2-toggle"
            className="text-sm font-semibold text-[#99582A] hover:text-[#111] transition-colors inline-flex items-center justify-center gap-2"
          >
            Add project details (optional) <ArrowRight size={15} />
          </button>
        </div>
      ) : (
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
          <button
            type="submit"
            disabled={submitting || uploading > 0}
            data-testid="lead-form-submit-btn"
            className="w-full md:w-auto bg-[#111] text-white hover:bg-[#99582A] transition-colors px-10 py-4 font-semibold tracking-wide flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send My Request
                <ArrowRight size={18} />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setStep(1)}
            data-testid="lead-form-back-btn"
            className="text-sm font-semibold text-[#4A4A4A] hover:text-[#111] transition-colors"
          >
            ← Back to contact info
          </button>
        </div>
      )}

      <p className="text-xs text-[#666] mt-4">
        No obligation. Free, on-site walk-through scheduled at your convenience.
      </p>
    </form>
  );
}

const inputCls =
  "w-full bg-transparent border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-3 outline-none transition-colors text-[#111] placeholder:text-[#999]";

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#2A2A2A]">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
